// eslint-disable-next-line import/no-extraneous-dependencies
import { SyntheticEvent } from "react"
import { SetURLSearchParams } from "react-router-dom"

import {
    DEVICE_MODEL_FILTER_KW,
    MAX_CAPACITY_KW,
    MIN_CAPACITY_KW,
    ORDER_BY_KW,
    ORDER_KW,
    SYSTEM_FILTER_REF_KW,
    TTR_DEFAULT_ORDER_BY,
    TTR_DEFAULT_ORDER_DIRECTION,
    IS_SUBSCRIBED,
    IS_THREAD_UNREAD,
} from "../constants/generalConstants"
import {
    CATEGORY_REF_KEY,
    COMPLIANCE_STATUS_REF_KEY,
    DEVICE_TYPE_REF_KEY,
    JURISDICTION_REF_KEY,
    MANUFACTURER_REF_KEY,
    PHASE_REF_KEY,
    TGenFilterRefKeys,
    TGenRefOutput,
    genFilterReferenceMapper,
} from "../services/middleware/mappers/genFilterReferenceMapper"
import {
    MAX_CAPACITY,
    TFilterSelect,
} from "../webpages/lowCarbonTechDatabases/genDeviceSearch"

/**
 * Hook for deriving and managing filter panel input values from URL search
 * params for TTR database search API. Search params are treated as the filter
 * panel state. This hook abstracts the translation of this state into objects
 * the filter panel inputs can read.
 *
 * @param searchParams - URL search parameters
 * @param setSearchParams - setter function for URL search parameters
 * @param referenceOptions - database lookup reference options
 * @param refOptionsLoading - loading state for database lookup reference options
 * @returns - helper function for setting a filter value and values for all
 * database filter panel options.
 */
export const useGenSearchURLFilterParams = (
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    referenceOptions: TGenRefOutput | undefined,
    refOptionsLoading: boolean
): {
    category: TFilterSelect
    manufacturer: TFilterSelect
    deviceType: TFilterSelect
    phases: TFilterSelect
    complianceStatus: TFilterSelect
    jurisdiction: TFilterSelect
    capacityMin: number
    capacityMax: number
    systemRef: string
    deviceModel: string
    orderBy: string
    orderDirection: string
    isSubscribed: boolean
    isThreadUnread: boolean
    setOrderBy: (orderBy: string, orderDirection: string) => void
    setFilterFromTextfield: (newValue: string, searchParam: string) => void
    clearAllFilters: () => void
    setFilterFromSlider: (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[]
    ) => void
    setFilterValue: (
        newValue: TFilterSelect,
        optionRefKey: TGenFilterRefKeys
    ) => void
} => {
    const optionMappings = genFilterReferenceMapper.idMappings
    /**
     * Called when a filter value has been updated. Writes new value to URL
     * search params and triggers re-render via `setSearchParams` function.
     * Only to be used with auto completes
     *
     * @param newValue - the new selected value
     * @param optionRefKey - reference key of the filter option mapping stored
     * in `filterReferenceMapper.idMappings`
     */
    const setFilterValueFromAutocompleteKwarg = (
        newValue: TFilterSelect,
        optionRefKey: TGenFilterRefKeys
    ): void => {
        const { optionId, searchParam } = optionMappings[optionRefKey]
        if (newValue) {
            searchParams.set(searchParam, String(newValue[optionId]))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    /**
     * Called when slider change is committed. Updates URL params.
     * @param e_ - MUI slider event, not used
     * @param newValue - array of upper and lower slider value
     */
    const setFilterFromSlider = (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[]
    ): void => {
        if (!Array.isArray(newValue)) {
            // TTR slider error: onSlideCommitted handle not returning array
            return
        }
        searchParams.delete(MIN_CAPACITY_KW)
        searchParams.delete(MAX_CAPACITY_KW)
        searchParams.set(
            MIN_CAPACITY_KW,
            String((newValue as [number, number])[0])
        )
        if ((newValue as [number, number])[1] !== MAX_CAPACITY) {
            searchParams.set(
                MAX_CAPACITY_KW,
                String((newValue as [number, number])[1])
            )
        }
        setSearchParams(searchParams)
    }

    /**
     * Updates URL search params for filter panel elements which are text
     * @param newValue - new value being set
     * @param searchParam - url search param for the textfield
     */
    const setFilterFromTextfield = (
        newValue: string,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    /**
     * Reads filter param value from URL and parses a filter state object.
     * URL param is an id. This function creates an {id: name} object, by
     * looking up the name value in reference data. Null is returned if the
     * reference data is loading.
     *
     * @param optionRefKey - reference key of the filter option mapping stored
     * in `filterReferenceMapper.idMappings`
     * @returns - value to be injected into `AppControlledSelect`
     */
    const filterSelectValueFromParams = (
        optionRefKey: TGenFilterRefKeys
    ): TFilterSelect => {
        const { optionId, searchParam } = optionMappings[optionRefKey]
        if (searchParams.has(searchParam)) {
            return refOptionsLoading
                ? null
                : ((referenceOptions?.[optionRefKey] as []) || []).find(
                      (option: Record<string, string | number>) =>
                          String(option[optionId]) ===
                          searchParams.get(searchParam)
                  ) || null
        }
        return null
    }

    /**
     * Sets order by and order direction URL params
     * @param orderBy - column to be sorted on
     * @param orderDirection - sort direction (asc | desc)
     */
    const setOrderBy = (orderBy: string, orderDirection: string): void => {
        searchParams.set(ORDER_BY_KW, orderBy)
        searchParams.set(ORDER_KW, orderDirection)
        setSearchParams(searchParams)
    }

    const clearAllFilters = (): void => {
        setSearchParams(new URLSearchParams())
    }

    // Extract filter panel state from URL
    const manufacturer = filterSelectValueFromParams(MANUFACTURER_REF_KEY)
    const category = filterSelectValueFromParams(CATEGORY_REF_KEY)
    const deviceType = filterSelectValueFromParams(DEVICE_TYPE_REF_KEY)
    const phases = filterSelectValueFromParams(PHASE_REF_KEY)
    const jurisdiction = filterSelectValueFromParams(JURISDICTION_REF_KEY)
    const complianceStatus = filterSelectValueFromParams(
        COMPLIANCE_STATUS_REF_KEY
    )
    const capacityMin = Number(searchParams.get(MIN_CAPACITY_KW)) || 0
    const capacityMax =
        Number(searchParams.get(MAX_CAPACITY_KW)) || MAX_CAPACITY
    const systemRef = searchParams.get(SYSTEM_FILTER_REF_KW) || ""
    const deviceModel = searchParams.get(DEVICE_MODEL_FILTER_KW) || ""

    // Sort params - these are defaulted
    const orderBy = searchParams.get(ORDER_BY_KW) || TTR_DEFAULT_ORDER_BY
    const orderDirection =
        searchParams.get(ORDER_KW) || TTR_DEFAULT_ORDER_DIRECTION
    const isSubscribed = searchParams.get(IS_SUBSCRIBED) === "true"
    const isThreadUnread = searchParams.get(IS_THREAD_UNREAD) === "true"

    return {
        capacityMin,
        capacityMax,
        category,
        complianceStatus,
        deviceModel,
        deviceType,
        jurisdiction,
        manufacturer,
        phases,
        orderBy,
        orderDirection,
        systemRef,
        setOrderBy,
        setFilterFromTextfield,
        setFilterValue: setFilterValueFromAutocompleteKwarg,
        setFilterFromSlider,
        clearAllFilters,
        isSubscribed,
        isThreadUnread,
    }
}
